"use client";
import {InlineFilter} from "@/components/@filter/inline-filter";
import {NeoDatePicker} from "@/components/forms";
import {NeoHeader} from "@/components/shared";
import {LoadingOverlay} from "@/components/table/neo-table/loaderNggrid";
import {NeoAGgrid} from "@/components/table/neo-table/neo-ag-grid";
import {useNeoContext} from "@/context/neo/neo.context";
import {convertMTOKM, formatDateTime} from "@/libs/helper";
import {reortsService} from "@/service/reports.service";
import dynamic from "next/dynamic";
import React, {Key, useEffect, useMemo, useState} from "react";
import toast from "react-hot-toast";

import "./ChartTooltip.css";
//import { Average } from "next/font/google";
const Chart = dynamic(() => import("react-apexcharts"), {ssr: false});

type Point = {x: number | string | Date; y: number};

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const a = (Math.PI / 180) * angleDeg;
  return {x: cx + r * Math.cos(a), y: cy + r * Math.sin(a)};
}

export const FuelAnalytics: React.FC<any> = ({reportdata}) => {
  const current = 0;
  const total = 50;
  const ticks: any = 44;
  const ratio = total > 0 ? Math.max(0, Math.min(1, current / total)) : 0;
  const activeTick = Math.round(ticks * ratio);
  const startAngle = 180;
  const endAngle = 360;
  const sweep = startAngle - endAngle;
  const w = 320;
  const h = 180; // ✅ crop to show ONLY top
  const cx = w / 2;
  const cy = 160; // ✅ center placed BELOW the SVG (so bottom is hidden)
  const rOuter = 120;
  const tickLen = 14;

  const defaultfuelsummary = {
    filled: 0,
    consumed: 0,
    average: 0,
    fueltheft: 0
  };
  const fuelChart = {
    currentFuelLevel: 0,
    totalFuelCapacity: 0,
    estimatedRangeKm: 0
  };

  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);

  const endOfDay = new Date();
  endOfDay.setHours(23, 59, 59, 999);

  const series: any = [
    {
      name: "Fuel Level",
      data: []
    }
  ];

  const {vehicles} = useNeoContext();
  const [loadingContext, setLoadingContext] = useState<boolean>(true);
  const [dataKey, setDataKey] = useState(0);
  const [chartKey, setChartKey] = useState(0);
  const [activeTicks, setActiveTicks] = useState(activeTick);
  const [fuelsummary, setFueSummary] = useState(defaultfuelsummary);
  const [fueldata, setFuelData] = useState(fuelChart);
  const [vehicle, setVehicle] = useState<Key | null>(null);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    from: startOfDay,
    to: endOfDay
  });
  const [fueltheft, setFuelTheft] = useState(false);
  const defaultvehicleKey = vehicles?.[0]?.trackerId
    ? String(vehicles[0].trackerId)
    : undefined;
  const [chartseries, setChartSeries] = useState(series);
  const getY = (p: any) => {
    if (typeof p === "number") return p;
    if (Array.isArray(p)) return p[1]; // [x,y] format
    return p?.y ?? 0; // {x,y} format
  };

  const maxY = useMemo(() => {
    const vals = chartseries.flatMap((s: any) => (s.data ?? []).map(getY));
    return vals.length ? Math.max(...vals) : 0;
  }, [chartseries]);

  const yMax = useMemo(() => Math.ceil(maxY * 1.1), [maxY]);

  const {xMin, xMax} = useMemo(() => {
    const xVals: number[] =
      chartseries?.flatMap((s: any) =>
        (s.data ?? []).map((p: any) => (Array.isArray(p) ? p[0] : p?.x))
      ) ?? [];

    const min = xVals.length ? Math.min(...xVals) : Date.now();
    const max = xVals.length ? Math.max(...xVals) : Date.now();

    return {xMin: min, xMax: max};
  }, [chartseries]);

  const {dayMin, dayMax, dayCount} = useMemo(() => {
    const start = new Date(xMin);
    start.setHours(0, 0, 0, 0);

    const end = new Date(xMax);
    end.setHours(23, 59, 59, 999);

    const oneDay = 24 * 60 * 60 * 1000;
    const count = Math.floor((end.getTime() - start.getTime()) / oneDay) + 1;

    return {
      dayMin: start.getTime(),
      dayMax: end.getTime(),
      dayCount: Math.max(count, 1)
    };
  }, [xMin, xMax]);

  const options: ApexCharts.ApexOptions = useMemo(
    () => ({
      chart: {
        type: "area",
        height: 220,
        toolbar: {show: false},
        zoom: {enabled: true},
        fontFamily: "Inter, system-ui, Arial, sans-serif"
      },
      stroke: {curve: "stepline", width: 3},
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 0,
          opacityFrom: 0.6,
          opacityTo: 0.5,
          stops: [0, 10, 15]
        }
      },
      dataLabels: {enabled: false},
      grid: {
        borderColor: "#EEF2F7",
        strokeDashArray: 0,
        padding: {left: 10, right: 10},
        yaxis: {lines: {show: false}},
        xaxis: {lines: {show: true}}
      },
      xaxis: {
        type: "datetime",
        min: dayMin,
        max: dayMax,
        tickAmount: dayCount,
        labels: {
          datetimeUTC: false,
          style: {colors: "#94A3B8"},
          formatter: (_v: string, ts: number, opts: any) => {
            const i = opts.i;

            const cur = new Date(ts);
            const curLabel = cur.toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short"
            });

            // get previous tick timestamp (not seriesX, this is tick labels)
            const prevTs = i > 0 ? opts.w.globals.labels[i - 1] : null;
            if (!prevTs) return curLabel;

            const prev = new Date(prevTs);
            const prevLabel = prev.toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short"
            });

            return curLabel;
          }
        },
        axisBorder: {show: true},
        axisTicks: {show: true},
        tooltip: {enabled: true}
      },
      yaxis: {
        min: 0,
        max: yMax,
        tickAmount: 5,
        labels: {
          style: {colors: "#94A3B8"},
          formatter: v => `${Math.round(v)} L`
        },
        title: {
          text: "Fuel Level (Lit)",
          style: {color: "#94A3B8", fontSize: "12px", fontWeight: 500}
        },
        axisBorder: {show: false},
        axisTicks: {show: false}
      },
      legend: {
        show: true,
        position: "top",
        fontSize: "12px",
        labels: {colors: "#64748B"},
        markers: {size: 6}
      },
      markers: {
        size: 0,
        hover: {size: 4}
      },
      tooltip: {
        shared: false,
        intersect: false,
        followCursor: true,
        custom: ({series, seriesIndex, dataPointIndex, w}) => {
          const rawVal = series?.[seriesIndex]?.[dataPointIndex];
          const val = typeof rawVal === "number" ? rawVal : 0;
          const xVal = w?.globals?.seriesX?.[seriesIndex]?.[dataPointIndex];
          const dateStr = formatDateTime(xVal);
          const name = w?.globals?.seriesNames?.[seriesIndex] ?? "Fuel Level";
          return `
          <div class="neo-tooltip">
            <div class="neo-tooltip__date">${dateStr}</div>
            <div class="neo-tooltip__row">
              <span class="neo-tooltip__title">${name}</span>
              <span class="neo-tooltip__value">${Math.round(val)} L</span>
            </div>
          </div>
        `;
        }
      },
      colors: ["#3B82F6"]
    }),
    [yMax]
  );

  const baseTableConfig = useMemo(
    () => ({
      title: reportdata?.activeReport?.menu
        ? reportdata?.activeReport?.menu
        : "Report",
      table: {
        columns: reportdata?.activeReport?.columns,
        activeColumns: reportdata?.activeReport?.activeColumns,
        activeFilters: reportdata?.activeReport?.filters,
        style: {
          height: "h-full"
        },
        meta: {
          id: 4,
          title: "",
          description: "Total Data",
          hasSearch: false,
          hasUpload: false,
          hasDownload: true,
          hasAdd: false,
          hasActions: false,
          hasCheckbox: false,
          hasFilter: false,
          hasFooter: true,
          hasHeader: true,
          hasPagination: true,
          hasCreate: false
        },
        url: {
          page: {},
          api: {
            get: reportdata?.activeReport?.url
          }
        },
        sidebar: reportdata?.activeReport?.sideBar,
        headerSidebar: reportdata?.activeReport?.headerSidebar,
        actions: [],
        innerFilters: reportdata?.activeReport?.innerFilters,
        tabledatas: []
      }
    }),
    [reportdata]
  );

  const [tableConfigData, setTableConfigData] = useState<any>(baseTableConfig);
  useEffect(() => {
    if (!vehicle && defaultvehicleKey) {
      setVehicle(defaultvehicleKey);
    }
  }, []);

  useEffect(() => {
    fueldatas();
  }, [selectedDateRange, vehicle]);

  const onDateChange = (event: any) => {
    setSelectedDateRange(event);
  };

  const onVehicleChange = (trackerId: any) => {
    setVehicle(trackerId);
  };

  const fueldatas = async () => {
    if (!vehicle || !selectedDateRange.from || !selectedDateRange.to) return;
    try {
      setLoadingContext(true);
      setTableConfigData(baseTableConfig);
      setFueSummary(defaultfuelsummary);
      setFuelData(fuelChart);
      setChartSeries((prev: any) =>
        prev.map((s: any) => (s.name === "Fuel Level" ? {...s, data: []} : s))
      );
      setActiveTicks(0);
      setDataKey(k => k + 1);
      setChartKey(j => j + 1);

      const apiurl = tableConfigData?.table?.url?.api?.get;
      const fromTs = new Date(selectedDateRange.from).setHours(0, 0, 0, 0);
      const toTs = new Date(selectedDateRange.to).setHours(23, 59, 59, 999);
      const body = {
        trackerId: vehicle,
        from: fromTs,
        to: toTs
      };

      // ✅ IMPORTANT: await here
      const res: any = await reortsService.onGetFuelReportData(apiurl, body);
      if (res?.data?.code !== 200) {
        toast.error(res?.data?.message || "Failed to fetch fuel report data");
        return;
      }
      const responsedata = res?.data?.data;
      // table
      const tableData = responsedata?.reportData ?? [];
      setTableConfigData((prev: any) => ({
        ...prev,
        table: {
          ...prev.table,
          tabledatas: Array.isArray(tableData) ? [...tableData] : []
        }
      }));
      setDataKey(k => k + 1);

      // summary
      const fuelSummaryData = responsedata?.statistics?.summary || {};
      setFueSummary({
        filled: fuelSummaryData.filled || 0,
        consumed: fuelSummaryData.consumed || 0,
        average: parseFloat(convertMTOKM(fuelSummaryData.avgMileage || 0)),
        fueltheft: fuelSummaryData.theft || 0
      });
      setFuelTheft((fuelSummaryData.theft || 0) > 0);

      // fuel status
      const fuelStatusData = responsedata?.statistics?.fuelStatus || {};
      const remaining = Number(fuelStatusData.remaining || 0);
      const total = Number(fuelStatusData.total || 0);
      setFuelData({
        currentFuelLevel: remaining,
        totalFuelCapacity: total,
        estimatedRangeKm: parseFloat(
          convertMTOKM(fuelStatusData.distanceCanDrive || 0)
        )
      });

      // ticks safe
      const tick = total > 0 ? (remaining / total) * ticks : 0;
      setActiveTicks(tick);

      // trends chart
      const trends = responsedata?.statistics?.trends || [];
      const trendsDatas: any[] = [];
      for (const day of trends) {
        for (const p of day?.trendsData ?? []) {
          trendsDatas.push([p.time, p.fuelLevel]);
        }
      }

      setChartSeries((prev: any) =>
        prev.map((s: any) =>
          s.name === "Fuel Level" ? {...s, data: trendsDatas} : s
        )
      );
      setChartKey(j => j + 1);
    } catch (err) {
      toast.error("Error fetching fuel report data");
    } finally {
      setLoadingContext(false);
    }
  };

  return (
    <div
      className={
        loadingContext
          ? "relative"
          : "flex min-h-[calc(100vh-63px)] w-full flex-col p-5"
      }
    >
      <div className="flex min-h-[calc(100vh-63px)] w-full flex-col p-5">
        <div className="mb-2 flex justify-between">
          <NeoHeader message="Fuel Analytics" />
          <div className="flex items-center justify-between gap-2">
            <div>
              <InlineFilter
                filtertype={2}
                vehicles={vehicles}
                defaultVehicleKey={defaultvehicleKey}
                onVehicleChange={onVehicleChange}
              />
              {/*<NeoSelect
                label=""
                id="vehicle"
                placeholder="Please select vehicle"
                size="lg"
                className="w-2/4 sm:w-72 md:w-80 lg:w-[95%]"
                defaultSelectedKeys={defaultvehicleKey}
                onSelectionChange={keys => {
                  const selectedtracker: any = Array.from(keys)[0]; // trackerId
                  setVehicle(selectedtracker);
                }}
                startContent={
                  <MdDirectionsBus className="h-6 w-6 text-[#1e2a44]" />
                }
              >
                {vehicles.map(item => (
                  <SelectItem key={item.trackerId} value={item.trackerId}>
                    {item.trackerName}
                  </SelectItem>
                ))}
              </NeoSelect>*/}
            </div>
            <div>
              <NeoDatePicker
                type={"range"}
                label=""
                handleDateChange={onDateChange}
                rangedefaultValue={selectedDateRange}
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-12 gap-2">
          <div className="col-span-4 grid grid-cols-2 gap-2">
            <div className="m rounded-lg border p-3 shadow-sm">
              <div className="mb-2 flex items-center gap-2">
                <div className="rounded-full bg-[#FFC72733]/20 p-2">
                  <svg
                    width="15"
                    height="18"
                    viewBox="0 0 15 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.38574 0.366211C7.89746 -0.12207 7.10449 -0.12207 6.61621 0.366211L0.366211 6.61621C-0.12207 7.10449 -0.12207 7.89746 0.366211 8.38574C0.854492 8.87402 1.64746 8.87402 2.13574 8.38574L6.25293 4.26465V16.249C6.25293 16.9404 6.81152 17.499 7.50293 17.499C8.19434 17.499 8.75293 16.9404 8.75293 16.249V4.26465L12.8701 8.38184C13.3584 8.87012 14.1514 8.87012 14.6396 8.38184C15.1279 7.89355 15.1279 7.10059 14.6396 6.6123L8.38965 0.362305L8.38574 0.366211Z"
                      fill="#FFC727"
                    />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-600">
                  Fuel Filled
                </span>
              </div>
              <p className="text-xl font-semibold text-gray-900">
                <span className="overflow-wrap-anywhere block whitespace-normal break-words">
                  {fuelsummary.filled} L
                </span>
              </p>
            </div>
            <div className="rounded-lg border p-3 shadow-sm">
              <div className="mb-2 flex items-center gap-2">
                <div className="rounded-full bg-[#FFC72733]/20 p-2">
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 17 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1.0625 2.12491C1.0625 0.952838 2.01543 -9.15527e-05 3.1875 -9.15527e-05H8.5C9.67207 -9.15527e-05 10.625 0.952838 10.625 2.12491V8.49991H10.8906C12.5043 8.49991 13.8125 9.80811 13.8125 11.4218V12.4843C13.8125 12.9259 14.1678 13.2812 14.6094 13.2812C15.051 13.2812 15.4062 12.9259 15.4062 12.4843V7.371C14.4898 7.13526 13.8125 6.30186 13.8125 5.31241V3.18741L12.75 2.12491C12.4578 1.83272 12.4578 1.3546 12.75 1.06241C13.0422 0.770221 13.5203 0.770221 13.8125 1.06241L16.3791 3.62901C16.7775 4.02745 17 4.56866 17 5.13311V5.57803V6.37491V7.43741V12.4843C17 13.8058 15.9309 14.8749 14.6094 14.8749C13.2879 14.8749 12.2188 13.8058 12.2188 12.4843V11.4218C12.2188 10.688 11.6244 10.0937 10.8906 10.0937H10.625V14.8749C11.2127 14.8749 11.6875 15.3497 11.6875 15.9374C11.6875 16.5251 11.2127 16.9999 10.625 16.9999H1.0625C0.474805 16.9999 0 16.5251 0 15.9374C0 15.3497 0.474805 14.8749 1.0625 14.8749V2.12491ZM3.1875 2.65616V5.84366C3.1875 6.13585 3.42656 6.37491 3.71875 6.37491H7.96875C8.26094 6.37491 8.5 6.13585 8.5 5.84366V2.65616C8.5 2.36397 8.26094 2.12491 7.96875 2.12491H3.71875C3.42656 2.12491 3.1875 2.36397 3.1875 2.65616Z"
                      fill="#FFC727"
                    />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-600">
                  Total Fuel Consumed
                </span>
              </div>
              <p className="text-xl font-semibold text-gray-900">
                <span className="overflow-wrap-anywhere block whitespace-normal break-words">
                  {fuelsummary.consumed} L
                </span>
              </p>
            </div>

            <div className="rounded-lg border p-2 shadow-sm">
              <div className="mb-2 flex items-center gap-2">
                <div className="rounded-full bg-[#FFC72733]/20 p-2">
                  <svg
                    width="19"
                    height="19"
                    viewBox="0 0 19 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M0 9.5C0 4.25681 4.25681 0 9.5 0C14.7432 0 19 4.25681 19 9.5C19 14.7432 14.7432 19 9.5 19C4.25681 19 0 14.7432 0 9.5ZM10.6875 3.5625C10.6875 2.9071 10.1554 2.375 9.5 2.375C8.8446 2.375 8.3125 2.9071 8.3125 3.5625C8.3125 4.2179 8.8446 4.75 9.5 4.75C10.1554 4.75 10.6875 4.2179 10.6875 3.5625ZM9.5 15.4375C10.81 15.4375 11.875 14.3725 11.875 13.0625C11.875 12.4168 11.6189 11.8342 11.2033 11.4074L13.582 6.00059C13.7787 5.55156 13.5746 5.02461 13.1256 4.82793Cnan nan 12.1496 4.83535 11.9529 5.28437L9.57051 10.6875C9.54824 10.6875 9.52227 10.6875 9.5 10.6875C8.19004 10.6875 7.125 11.7525 7.125 13.0625Cnan nan 8.19004 15.4375 9.5 15.4375ZM6.53125 5.34375C6.53125 4.68835 5.99915 4.15625 5.34375 4.15625C4.68835 4.15625 4.15625 4.68835 4.15625 5.34375C4.15625 5.99915 4.68835 6.53125 5.34375 6.53125C5.99915 6.53125 6.53125 5.99915 6.53125 5.34375ZM3.5625 10.6875C4.2179 10.6875 4.75 10.1554 4.75 9.5C4.75 8.8446 4.2179 8.3125 3.5625 8.3125C2.9071 8.3125 2.375 8.8446 2.375 9.5C2.375 10.1554 2.9071 10.6875 3.5625 10.6875ZM16.625 9.5C16.625 8.8446 16.0929 8.3125 15.4375 8.3125C14.7821 8.3125 14.25 8.8446 14.25 9.5C14.25 10.1554 14.7821 10.6875 15.4375 10.6875C16.0929 10.6875 16.625 10.1554 16.625 9.5Z"
                      fill="#FFC727"
                    />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-600">
                  Average Mileage
                </span>
              </div>
              <p className="text-xl font-semibold text-gray-900">
                <span className="overflow-wrap-anywhere block whitespace-normal break-words">
                  {fuelsummary.average} KM/L
                </span>
              </p>
            </div>

            {fueltheft ? (
              <div className="rounded-xl border border-red-300 bg-red-50 p-2 shadow-sm">
                <div className="mb-2 flex items-center gap-2">
                  <div className="flex justify-start p-1">
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 48 48"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="1"
                        y="1"
                        width="46"
                        height="46"
                        rx="23"
                        fill="white"
                        fill-opacity="0.6"
                      />
                      <rect
                        x="1"
                        y="1"
                        width="46"
                        height="46"
                        rx="23"
                        stroke="#FECACA"
                        stroke-width="2"
                      />
                      <path
                        d="M24.0002 13.5C24.6658 13.5 25.2799 13.8516 25.6174 14.4281L35.7424 31.6781C36.0846 32.2594 36.0846 32.9766 35.7518 33.5578C35.419 34.1391 34.7955 34.5 34.1252 34.5H13.8752C13.2049 34.5 12.5815 34.1391 12.2487 33.5578C11.9158 32.9766 11.9205 32.2547 12.258 31.6781L22.383 14.4281C22.7205 13.8516 23.3346 13.5 24.0002 13.5ZM24.0002 19.5C23.3768 19.5 22.8752 20.0016 22.8752 20.625V25.875C22.8752 26.4984 23.3768 27 24.0002 27Cnan nan 25.1252 26.4984 25.1252 25.875V20.625C25.1252 20.0016 24.6237 19.5 24.0002 19.5ZM25.5002 30C25.5002 29.1721 24.8281 28.5 24.0002 28.5C23.1724 28.5 22.5002 29.1721 22.5002 30C22.5002 30.8279 23.1724 31.5 24.0002 31.5C24.8281 31.5 25.5002 30.8279 25.5002 30Z"
                        fill="#B91C1C"
                      />
                    </svg>
                  </div>
                  <div className="justify-center">
                    <p className="mt-2 text-sm font-semibold text-[#6B7280]">
                      Fuel Theft Alert
                    </p>
                    <div className="justify-center rounded-full bg-[#FEE2E2] p-1 text-center text-xs text-[#991B1B]">
                      <div className="flex items-center gap-1">
                        <div className="flex justify-center">
                          <svg
                            width="6"
                            height="6"
                            viewBox="0 0 6 6"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M0 3C0 4.65575 1.34425 6 3 6C4.65575 6 6 4.65575 6 3C6 1.34425 4.65575 0 3 0C1.34425 0 0 1.34425 0 3Z"
                              fill="#EF4444"
                            />
                          </svg>
                        </div>
                        <p>Action Required</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-1 justify-center text-center">
                  <p className="text-3xl font-extrabold text-red-700">
                    {fuelsummary.fueltheft}
                  </p>
                </div>
                <div className="text-sm font-medium text-slate-500">
                  Abnormal fuel drops detected
                </div>
              </div>
            ) : (
              <div className="item-center rounded-lg border border-[#4ADE80] bg-[#E7FCF0] p-2 shadow-sm">
                <div className="mb-4 flex items-center gap-2">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 48 48"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="1"
                      y="1"
                      width="46"
                      height="46"
                      rx="23"
                      fill="white"
                      fill-opacity="0.6"
                    />
                    <rect
                      x="1"
                      y="1"
                      width="46"
                      height="46"
                      rx="23"
                      stroke="#BBF7D0"
                      stroke-width="2"
                    />
                    <path
                      d="M24 12C24.2156 12 24.4313 12.0469 24.6281 12.1359L33.4547 15.8812C34.486 16.3172 35.2547 17.3344 35.25 18.5625C35.2266 23.2125 33.3141 31.7203 25.2375 35.5875C24.4547 35.9625 23.5453 35.9625 22.7625 35.5875C14.686 31.7203 12.7735 23.2125 12.75 18.5625C12.7453 17.3344 13.5141 16.3172 14.5453 15.8812L23.3766 12.1359C23.5688 12.0469 23.7844 12 24 12ZM24 15.1312V32.85C30.4688 29.7188 32.2078 22.7859 32.25 18.6281L24 15.1312Z"
                      fill="#15803D"
                    />
                  </svg>
                  <div>
                    <p className="mt-2 text-sm font-semibold text-[#6B7280]">
                      Fuel Theft Status
                    </p>
                    <div className="flex items-center justify-center rounded-full border border-[#4ADE80] text-center text-xs text-[#166534]">
                      <div className="flex items-center gap-2">
                        <div>
                          <svg
                            width="6"
                            height="6"
                            viewBox="0 0 6 6"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M0 3C0 4.65575 1.34425 6 3 6C4.65575 6 6 4.65575 6 3C6 1.34425 4.65575 0 3 0C1.34425 0 0 1.34425 0 3Z"
                              fill="#22C55E"
                            ></path>
                          </svg>
                        </div>
                        <p>Fuel Secure</p>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-center text-xs font-semibold text-[#6B7280]">
                  <span className="overflow-wrap-anywhere break-word block whitespace-normal">
                    No fuel theft detected during this period.
                  </span>
                </p>
              </div>
            )}
          </div>
          <div className="col-span-5 rounded-lg border bg-white p-2 shadow-sm">
            <Chart
              key={`${chartKey}`}
              options={options}
              series={chartseries}
              type="area"
              height={240}
            />
          </div>
          <div className="col-span-3 rounded-lg border bg-white p-6 shadow-sm ">
            <p className="text-center text-sm font-semibold text-slate-400">
              Current Fuel Level
            </p>
            <p className="mt-1 text-center text-xl font-semibold text-gray-900">
              {fueldata.currentFuelLevel}L remaining of{" "}
              {fueldata.totalFuelCapacity}L
            </p>
            <div className="relative -mt-8 flex items-center justify-center">
              <svg
                width={w}
                height={h}
                viewBox={`0 0 ${w} ${h}`}
                className="block"
              >
                {Array.from({length: ticks}).map((_, i) => {
                  const denom = Math.max(1, ticks - 1);
                  const t = i / denom;
                  const ang = startAngle - t * sweep;

                  const p1 = polarToCartesian(cx, cy, rOuter, ang);
                  const p2 = polarToCartesian(cx, cy, rOuter - tickLen, ang);

                  const isActive = i < activeTicks;

                  return (
                    <line
                      key={i}
                      x1={p1.x}
                      y1={p1.y}
                      x2={p2.x}
                      y2={p2.y}
                      stroke={isActive ? "#22c55e" : "#e5e7eb"}
                      strokeWidth="5"
                      strokeLinecap="round"
                    />
                  );
                })}
              </svg>

              <div className="absolute top-[90px] flex flex-col items-center">
                <p className="text-sm font-semibold text-slate-400">
                  Fuel Level
                </p>
                <p className="text-4xl font-extrabold text-emerald-500">
                  {fueldata.currentFuelLevel}L
                </p>

                <p className="mt-4 text-sm font-semibold text-slate-400">
                  You can drive approximately
                </p>

                <div className="mt-1 flex items-center gap-2">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17.1994 0.756671C17.1887 0.573189 17.111 0.400028 16.981 0.270066C16.8511 0.140103 16.6779 0.0623864 16.4944 0.0516713C9.35723 -0.367391 3.64035 1.78136 1.20285 5.81261C0.357943 7.19159 -0.0584077 8.79043 0.00660103 10.4064C0.0490345 11.4397 0.25928 12.4593 0.629101 13.4251C0.650832 13.4845 0.687252 13.5375 0.734971 13.5791C0.782689 13.6207 0.840153 13.6495 0.902009 13.6629C0.963866 13.6763 1.0281 13.6738 1.08874 13.6556C1.14937 13.6375 1.20443 13.6043 1.24879 13.5592L9.21754 5.46855C9.28722 5.39886 9.36995 5.34359 9.46099 5.30588C9.55204 5.26816 9.64962 5.24875 9.74816 5.24875C9.84671 5.24875 9.94429 5.26816 10.0353 5.30588C10.1264 5.34359 10.2091 5.39886 10.2788 5.46855C10.3485 5.53823 10.4037 5.62095 10.4415 5.712C10.4792 5.80304 10.4986 5.90062 10.4986 5.99917C10.4986 6.09772 10.4792 6.1953 10.4415 6.28634C10.4037 6.37739 10.3485 6.46011 10.2788 6.5298L1.57035 15.3695L0.240038 16.6998C0.101687 16.8345 0.0190905 17.0163 0.00866848 17.2091C-0.00175355 17.4019 0.0607628 17.5916 0.183788 17.7404C0.251164 17.8184 0.3339 17.8817 0.426832 17.9264C0.519763 17.971 0.620895 17.996 0.723913 17.9997C0.82693 18.0035 0.929622 17.986 1.02558 17.9483C1.12153 17.9107 1.20868 17.8536 1.2816 17.7807L2.85566 16.2067C4.18129 16.8479 5.5191 17.1976 6.84566 17.2445C6.95004 17.2482 7.0541 17.2501 7.15785 17.2501C8.66854 17.254 10.1506 16.8379 11.4385 16.0482C15.4697 13.6107 17.6194 7.8948 17.1994 0.756671Z"
                      fill="#10D465"
                    />
                  </svg>
                  <p className="text-2xl font-extrabold text-slate-900">
                    {fueldata.estimatedRangeKm}{" "}
                    <span className="text-2xl font-extrabold">KM</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <NeoAGgrid key={`${dataKey}`} table={tableConfigData?.table} />
      </div>
      {loadingContext && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <LoadingOverlay />
        </div>
      )}
    </div>
  );
};
